import { default as React, useCallback, useState } from 'react';
import {
  Block,
  Color,
  Column,
  Row,
  Selector,
  SelectorOptionType,
  splitBorderWidthProps,
  splitPaddingProps,
  Text,
  TextLink,
  useTelemetry,
  OverflowTooltip,
} from '@snowflake/core-ui';
import { SignupFormFields, useSignupFormData } from './SignupFormDataContext';
import { FormattedMessage, useIntl } from 'react-intl';
import { CardTemplate } from '../../shared/CardTemplate';
import {
  ABExperimentName,
  countries,
  FeatureFlag,
  nonPriorityCountries,
  priorityCountries,
  isCocoSubscriptionPromoFlow,
  isCortexCodeFlow,
} from '@signup/shared';
import { SignupCardButton } from './SignupCardButton';
import { SignupFormInputs } from './SignupFormInputs';
import { useSignupFormController } from './SignupFormControllerContext';
import { EmailAgreement } from '../../shared/EmailAgreement';
import { useStepOneModifiedInputConfigs } from './inputConfigs';
import { InlinedSelector } from '../../shared/InlinedSelector';
import { SignupEventName } from '../../../../utils/SignupLogger';
import * as configs from '../../../../utils/configurations';
import { useSignupFlowType } from '../../../../utils/useSignupFlowType';
import { SocialLoginButtons } from '../../../SocialLoginButtons/SocialLoginButtons';

interface Props {
  continueNext: () => void;
  onPlanOptionsClick: () => void;
}

const FormOneFieldNames: SignupFormFields[] = [
  'firstName',
  'lastName',
  'email',
  'companyName',
  'jobTitle',
  'countryIsoCode',
];

const CLOUD_LOGO_PATHS = {
  aws: '/static/images/cloud_provider_choices/aws-logo.svg',
  azure: '/static/images/cloud_provider_choices/microsoft-logo.svg',
  gcp: '/static/images/cloud_provider_choices/gcp-logo.png',
} as const;

interface EditionPreviewProps {
  edition: string;
}

const EditionPreview = ({ edition }: EditionPreviewProps) => {
  return (
    <Text weight={400} color={Color.Gray70} fontSize={12}>
      {edition} Edition
    </Text>
  );
};

interface CloudIconProps {
  cloud: string;
}

const CloudIcon = ({ cloud }: CloudIconProps) => {
  const logoSrc = CLOUD_LOGO_PATHS[cloud as keyof typeof CLOUD_LOGO_PATHS];
  if (!logoSrc) return null;

  return (
    <Block display="inline-flex" alignItems="center" flexShrink={0} marginRight={8}>
      <img src={logoSrc} alt={cloud.toUpperCase()} style={{ height: '12px', width: 'auto' }} />
    </Block>
  );
};

interface CloudRegionPreviewProps {
  cloud: string;
  regionName: string;
}

const CloudRegionPreview = ({ cloud, regionName }: CloudRegionPreviewProps) => {
  if (!regionName) {
    return <CloudIcon cloud={cloud} />;
  }

  return (
    <Block display="flex" flexDirection="row" alignItems="center" flexWrap="nowrap" minWidth={0}>
      <CloudIcon cloud={cloud} />
      <Text fontSize={12} color={Color.Gray70} weight={400} minWidth={0} flexShrink={1}>
        <OverflowTooltip>{regionName}</OverflowTooltip>
      </Text>
    </Block>
  );
};

export const SignupFormStepOneModified = (props: Props) => {
  const signupFlowType = useSignupFlowType();
  const isCortexCodeCLI = isCortexCodeFlow(signupFlowType);
  const formData = useSignupFormData();
  const {
    setField,
    countryIsoCode,
    optInEmailAgreement,
    optOutEmailAgreement,
    edition,
    cloud,
    region,
  } = formData;
  const { regionSelectList, signupCardProps } = useSignupFormController();
  const { formatMessage } = useIntl();
  const { continueNext, onPlanOptionsClick } = props;
  const { validateField, submitSignupForm } = useSignupFormController();
  const isCortexCodeV2 = configs.isFeatureEnabled(FeatureFlag.FF_ENABLE_CORTEX_CODE_2026);
  // CoCo subscription promo signups only link the Privacy Notice below the Sign up button.
  const showPrivacyNoticeOnly = isCocoSubscriptionPromoFlow(signupFlowType);
  const showSocialLogin = configs.isFeatureEnabled(FeatureFlag.FF_ENABLE_OIDC_SIGNUP);
  const [forceRequired, setForceRequired] = useState(false);
  const inputConfigs = useStepOneModifiedInputConfigs();
  const { logAction } = useTelemetry();
  const [formOneStartTime, setFormOneStartTime] = useState<number>(0);

  const getRegionDisplayName = useCallback(() => {
    if (!region || !cloud) return '';
    const regionList = regionSelectList[cloud];
    const regionOption = regionList?.find(r => r.value === region);
    return regionOption?.label || '';
  }, [region, cloud, regionSelectList]);

  const countryOptions: SelectorOptionType<string>[] = [
    ...priorityCountries.map(country => {
      return {
        value: country.isoCode,
        label: country.label,
      };
    }),
    { divider: true },
    ...nonPriorityCountries.map(country => {
      return {
        value: country.isoCode,
        label: country.label,
      };
    }),
  ];

  const onGetStartedClicked = useCallback(async () => {
    const invalidFieldNames = FormOneFieldNames.filter(
      fieldName => validateField(fieldName, formData[fieldName]) !== true,
    );

    if (invalidFieldNames.length > 0) {
      logAction(
        SignupEventName.SIGNUP_MODIFIED_FORM_ONE_VALIDATION_ERROR,
        'ui_click',
        'SignupCardModified',
        {
          firstFormValidationErrors: invalidFieldNames,
          flowType: 'modified',
        },
      );

      setForceRequired(true);
      return;
    }

    await new Promise(resolve => setTimeout(resolve, 0));

    logAction(SignupEventName.SIGNUP_MODIFIED_FORM_ONE_COMPLETE, 'ui_click', 'SignupCardModified', {
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      email: formData.email.trim().toLowerCase(),
      company: formData.companyName,
      role: formData.jobTitle,
      country: countries.find(c => c.isoCode === countryIsoCode)?.value || '',
      formId: formOneStartTime.toString(),
      formOneCompletionTime: Date.now() / 1000 - formOneStartTime,
      flowType: 'modified',
    });

    // Log that we're clicking the Signup CTA (which triggers account creation in modified flow)
    logAction(
      SignupEventName.SIGNUP_MODIFIED_SIGNUP_CTA_CLICKED,
      'ui_click',
      'SignupCardModified',
      {
        email: formData.email.trim().toLowerCase(),
        company: formData.companyName,
        country: countries.find(c => c.isoCode === countryIsoCode)?.value || '',
        edition: edition || 'Enterprise',
        cloud: cloud || 'aws',
        region: region,
        flowType: 'modified',
        formOneCompletionTime: Date.now() / 1000 - formOneStartTime,
      },
    );

    continueNext();

    const formTwoCompletionTime = Date.now() / 1000 - formOneStartTime;
    await submitSignupForm({ formTwoCompletionTime });
  }, [
    continueNext,
    validateField,
    formData,
    setField,
    logAction,
    submitSignupForm,
    formOneStartTime,
    countryIsoCode,
    edition,
    cloud,
    region,
  ]);

  const privacyNoticeLink = (
    <TextLink href="https://www.snowflake.com/privacy-policy/" target="_blank" fontSize="12px">
      Privacy Notice
    </TextLink>
  );

  const planOptionsButton = (
    <Row>
      <Block
        as="button"
        type="button"
        onClick={onPlanOptionsClick}
        cursor="pointer"
        width="100%"
        backgroundColor="transparent"
        {...splitPaddingProps([8, 0])}
        display="flex"
        flexDirection="row"
        alignItems="center"
        justifyContent="flex-start"
        textAlign="left"
        fontFamily="inherit"
        {...{
          ':hover': {
            opacity: 0.75,
          },
          ':focus': {
            outline: 'none',
          },
        }}
      >
        <Block
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="space-around"
          flex={1}
          minWidth={0}
          flexWrap="nowrap"
        >
          <EditionPreview edition={edition} />
          {cloud && (
            <Block minWidth={0} flexShrink={1}>
              <CloudRegionPreview cloud={cloud} regionName={getRegionDisplayName()} />
            </Block>
          )}
          <Text fontSize={12} color={Color.Gray60} flexShrink={0}>
            ›
          </Text>
        </Block>
      </Block>
    </Row>
  );

  return (
    <CardTemplate>
      <Column
        width="100%"
        rowGap="16px"
        onFocus={() => !formOneStartTime && setFormOneStartTime(Date.now() / 1000)}
      >
        <Row>
          <Column width="100%">
            <Text fontSize="20px" fontWeight="600">
              {isCortexCodeCLI && !isCortexCodeV2 ? (
                <FormattedMessage id="Sign up for Cortex Code CLI" />
              ) : (
                <FormattedMessage id="Create a Snowflake account" />
              )}
            </Text>
            <Text color={Color.Gray70}>
              <FormattedMessage
                id="Already have an account? <signin>Sign in</signin>"
                values={{
                  // eslint-disable-next-line
                  signin: (...chunks: any[]) => {
                    let snowflakeLoginUrl = 'https://app.snowflake.com/';

                    //If there are any queryParams, persist them on the login screen
                    const queryParams = window?.location?.search ?? '';
                    if (queryParams) {
                      snowflakeLoginUrl += queryParams;
                    }

                    return (
                      <TextLink
                        href={snowflakeLoginUrl}
                        color={Color.Blue50}
                        onClick={() =>
                          logAction(
                            SignupEventName.GOTO_LOGIN_PAGE_LINK_CLICKED,
                            'ui_click',
                            'SignupPage',
                            {},
                          )
                        }
                        fontWeight="600"
                      >
                        {chunks}
                      </TextLink>
                    );
                  },
                }}
              />
            </Text>
          </Column>
        </Row>
        {showSocialLogin && planOptionsButton}
        {showSocialLogin && (
          <Row>
            <Column width="100%">
              <SocialLoginButtons formId={signupCardProps.formId} />
            </Column>
          </Row>
        )}
        <Row>
          <SignupFormInputs configs={inputConfigs} forceRequired={forceRequired} />
        </Row>

        <Row>
          <EmailAgreement
            optInEmailAgreement={optInEmailAgreement}
            setOptInEmailAgreement={val => setField('optInEmailAgreement', val)}
            optOutEmailAgreement={optOutEmailAgreement}
            setOptOutEmailAgreement={val => setField('optOutEmailAgreement', val)}
            countryIsoCode={countryIsoCode}
          />
        </Row>

        <Row>
          <SignupCardButton width="100%" onClick={onGetStartedClicked}>
            <FormattedMessage id="Sign up" />
          </SignupCardButton>
        </Row>

        <Row textAlign="center" align={showPrivacyNoticeOnly ? 'center' : undefined}>
          <Text fontSize="12px" color={Color.Gray60}>
            {showPrivacyNoticeOnly ? (
              privacyNoticeLink
            ) : (
              <FormattedMessage
                id="By clicking Sign up, you agree to the <tos>Snowflake Self Service On Demand Terms</tos> and understand that Snowflake will process your personal information in accordance with its <privacy>Privacy Notice</privacy>."
                defaultMessage="By clicking Sign up, you agree to the {tos1} and our {tos2}."
                values={{
                  tos1: (
                    <TextLink
                      href="https://www.snowflake.com/wp-content/uploads/2017/11/Self-Serve-On-Demand-TOS.pdf"
                      target="_blank"
                      fontSize="12px"
                    >
                      Snowflake Self Service On Demand Terms
                    </TextLink>
                  ),
                  tos2: privacyNoticeLink,
                }}
              />
            )}
          </Text>
        </Row>
        {!showSocialLogin && planOptionsButton}
        <Row align="center" fontSize="12px" color={Color.Gray70} columnGap="8px">
          <Column verticalAlign="middle">
            <Text>
              <FormattedMessage id="Country:" />
            </Text>
          </Column>
          <Column>
            {configs.getExperimentGroup(
              ABExperimentName.SIGNUP_FORM_REWORK_COREUI_COUNTRY_SELECTOR,
            ) === 'ENABLED' ? (
              <Selector
                {...splitBorderWidthProps(0)}
                options={countryOptions}
                value={countryIsoCode}
                placeholder={formatMessage({ id: 'Country' }) + '*'}
                onChange={e => {
                  setField('countryIsoCode', e.currentTarget.value);
                }}
                flexGrow="0"
              />
            ) : (
              <InlinedSelector
                {...splitBorderWidthProps(0)}
                options={countryOptions}
                value={countryIsoCode}
                label={countries.find(c => c.isoCode === countryIsoCode)?.label}
                placeholder={formatMessage({ id: 'Select a country' })}
                selectValue={item => {
                  setField('countryIsoCode', item);
                }}
                buttonTextColor={forceRequired && !countryIsoCode ? Color.Red50 : undefined}
              />
            )}
          </Column>
        </Row>
      </Column>
    </CardTemplate>
  );
};
